import { supabase, supabaseAdmin, VIDEOS_BUCKET, OUTPUTS_BUCKET } from './supabase'

export interface StorageFile {
  name: string
  id: string
  updated_at: string
  created_at: string
  last_accessed_at: string
  metadata: Record<string, any>
}

export interface UploadResult {
  path: string
  url: string
  size: number
  type: string
}

export class StorageManager {
  private buckets = {
    videos: VIDEOS_BUCKET,
    outputs: OUTPUTS_BUCKET
  }

  // Sanitize filename for storage compatibility
  private sanitizeFilename(filename: string): string {
    return filename
      // Replace spaces with underscores
      .replace(/\s+/g, '_')
      // Remove or replace special characters (keep only alphanumeric, dots, dashes, underscores)
      .replace(/[^a-zA-Z0-9._-]/g, '')
      // Remove multiple consecutive underscores
      .replace(/_{2,}/g, '_')
      // Remove leading/trailing underscores and dots
      .replace(/^[._]+|[._]+$/g, '')
      // Ensure we don't have an empty filename
      || 'file'
  }

  // Initialize storage buckets if they don't exist (requires admin client)
  async initializeBuckets() {
    if (!supabaseAdmin) {
      console.warn('Admin client not available. Skipping bucket initialization.')
      return
    }

    try {
      const buckets = await supabaseAdmin.storage.listBuckets()
      
      for (const bucketName of Object.values(this.buckets)) {
        const exists = buckets.data?.some((bucket: any) => bucket.name === bucketName)
        
        if (!exists) {
          const { error } = await supabaseAdmin.storage.createBucket(bucketName, {
            public: false,
            allowedMimeTypes: ['video/*', 'image/*', 'application/*'],
            fileSizeLimit: 52428800 // 50MB
          })
          
          if (error) {
            console.error(`Failed to create bucket ${bucketName}:`, error)
          }
        }
      }
    } catch (error) {
      console.error('Failed to initialize storage buckets:', error)
    }
  }

  // Upload file with progress tracking simulation
  async uploadFile(
    bucket: keyof typeof this.buckets,
    file: File,
    path?: string,
    onProgress?: (progress: number) => void
  ): Promise<UploadResult> {
    const bucketName = this.buckets[bucket]
    const sanitizedFilename = this.sanitizeFilename(file.name)
    const filePath = path || `${Date.now()}_${sanitizedFilename}`
    
    try {
      // Progress tracking simulation
      if (onProgress) {
        onProgress(0) // Start
        
        // Simulate progress with small delays for better UX
        setTimeout(() => onProgress(20), 100)
        setTimeout(() => onProgress(40), 200)
        setTimeout(() => onProgress(60), 300)
      }

      const { error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        })

      if (error) throw error

      if (onProgress) {
        onProgress(80) // Upload complete
      }

      const { data: urlData } = await supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath)

      if (onProgress) {
        onProgress(100) // Finished
      }

      return {
        path: filePath,
        url: urlData.publicUrl,
        size: file.size,
        type: file.type
      }
    } catch (error) {
      console.error('Upload failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      throw new Error(`Failed to upload file: ${errorMessage}`)
    }
  }

  // Download file
  async downloadFile(bucket: keyof typeof this.buckets, path: string): Promise<Blob> {
    const bucketName = this.buckets[bucket]
    
    try {
      const { data, error } = await supabase.storage
        .from(bucketName)
        .download(path)

      if (error) throw error
      return data
    } catch (error) {
      console.error('Download failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      throw new Error(`Failed to download file: ${errorMessage}`)
    }
  }

  // Get file URL (public or signed)
  async getFileUrl(
    bucket: keyof typeof this.buckets,
    path: string,
    signed: boolean = false,
    expiresIn: number = 3600
  ): Promise<string> {
    const bucketName = this.buckets[bucket]
    
    if (signed) {
      const { data, error } = await supabase.storage
        .from(bucketName)
        .createSignedUrl(path, expiresIn)
      
      if (error) throw error
      return data.signedUrl
    } else {
      const { data } = supabase.storage
        .from(bucketName)
        .getPublicUrl(path)
      
      return data.publicUrl
    }
  }

  // List files in bucket/folder
  async listFiles(
    bucket: keyof typeof this.buckets,
    folder?: string,
    limit: number = 100,
    offset: number = 0
  ): Promise<StorageFile[]> {
    const bucketName = this.buckets[bucket]
    
    try {
      const { data, error } = await supabase.storage
        .from(bucketName)
        .list(folder || '', {
          limit,
          offset,
          sortBy: { column: 'created_at', order: 'desc' }
        })

      if (error) throw error
      
      return data.map((file: any)  => ({
        name: file.name,
        id: file.id,
        updated_at: file.updated_at,
        created_at: file.created_at,
        last_accessed_at: file.last_accessed_at,
        metadata: file.metadata || {}
      }))
    } catch (error) {
      console.error('Failed to list files:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      throw new Error(`Failed to list files: ${errorMessage}`)
    }
  }

  // Delete file
  async deleteFile(bucket: keyof typeof this.buckets, path: string): Promise<boolean> {
    const bucketName = this.buckets[bucket]
    
    try {
      const { error } = await supabase.storage
        .from(bucketName)
        .remove([path])

      if (error) throw error
      return true
    } catch (error) {
      console.error('Delete failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      throw new Error(`Failed to delete file: ${errorMessage}`)
    }
  }

  // Move file between buckets or folders
  async moveFile(
    sourceBucket: keyof typeof this.buckets,
    sourcePath: string,
    targetBucket: keyof typeof this.buckets,
    targetPath: string
  ): Promise<boolean> {
    try {
      // Download from source
      const fileBlob = await this.downloadFile(sourceBucket, sourcePath)
      
      // Upload to target
      const file = new File([fileBlob], sourcePath.split('/').pop() || 'file')
      await this.uploadFile(targetBucket, file, targetPath)
      
      // Delete from source
      await this.deleteFile(sourceBucket, sourcePath)
      
      return true
    } catch (error) {
      console.error('Move failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      throw new Error(`Failed to move file: ${errorMessage}`)
    }
  }

  // Get storage usage statistics
  async getStorageStats(): Promise<Record<string, { files: number; size: number }>> {
    const stats: Record<string, { files: number; size: number }> = {}
    
    try {
      for (const [bucketKey, ] of Object.entries(this.buckets)) {
        const files = await this.listFiles(bucketKey as keyof typeof this.buckets)
        
        stats[bucketKey] = {
          files: files.length,
          size: files.reduce((total, file) => total + (file.metadata?.size || 0), 0)
        }
      }
      
      return stats
    } catch (error) {
      console.error('Failed to get storage stats:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
      throw new Error(`Failed to get storage stats: ${errorMessage}`)
    }
  }
}

// Export singleton instance
export const storageManager = new StorageManager()

// Utility function to sanitize filenames for public use
export function sanitizeFilename(filename: string): string {
  return filename
    // Replace spaces with underscores
    .replace(/\s+/g, '_')
    // Remove or replace special characters (keep only alphanumeric, dots, dashes, underscores)
    .replace(/[^a-zA-Z0-9._-]/g, '')
    // Remove multiple consecutive underscores
    .replace(/_{2,}/g, '_')
    // Remove leading/trailing underscores and dots
    .replace(/^[._]+|[._]+$/g, '')
    // Ensure we don't have an empty filename
    || 'file'
}
